import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as api from 'api';
import { AppDispatch } from 'store';
import { setError } from './general';
import { ResultCategory } from '../constants';

export type ClubsResults = {
  [clubName: string]: {
    total: number;
    perCategory: {
      [category: string]: number;
    };
  };
};

export type ResultsState = {
  perCategory: {
    [category: string]: Results;
  };
  clubs?: ClubsResults;
  routeNumbers?: RouteNumbersConfig['perCategory'];
};

const initialState: ResultsState = {
  perCategory: {},
  clubs: undefined,
  routeNumbers: undefined,
};

export const resultsSlice = createSlice({
  name: 'results',
  initialState,
  reducers: {
    setResults: (
      state,
      {
        payload: { category, results },
      }: PayloadAction<{ category: ResultCategory; results: Results }>,
    ) => {
      state.perCategory[category] = results;
    },
    setClubsResults: (state, { payload }: PayloadAction<ClubsResults>) => {
      state.clubs = payload;
    },
    setRouteNumbers: (
      state,
      { payload }: PayloadAction<RouteNumbersConfig['perCategory']>,
    ) => {
      state.routeNumbers = payload;
    },
  },
});

const { setResults, setClubsResults, setRouteNumbers } = resultsSlice.actions;

export function fetchResults(category: ResultCategory) {
  return async (dispatch: AppDispatch) => {
    try {
      const results = await api.fetchResults(category);

      if (category === 'clubs') {
        dispatch(setClubsResults(results as unknown as ClubsResults));
      } else {
        dispatch(setResults({ category, results }));
      }
    } catch (err) {
      console.error('Failed to fetch results', err);
      dispatch(setError('Failed to fetch results'));
    }
  };
}

export function fetchRouteNumbers() {
  return async (dispatch: AppDispatch) => {
    try {
      const routeNumbers = await api.getRouteNumbers();

      dispatch(setRouteNumbers(routeNumbers.perCategory));
    } catch (err) {
      console.error('Failed to fetch route numbers', err);
      dispatch(setError('Failed to fetch route numbers'));
    }
  };
}

export default resultsSlice.reducer;
